import type {
  CommerceLanding,
  DocumentTypeOption,
  HomeAction,
} from '../../../domain/commerce-registry/models/commerce-landing.model';
import type { CommerceRegistryThemePalette } from '../../../domain/commerce-registry/models/commerce-registry-theme-palette.model';
import type { LandingDinamicaRtdb } from '../../../domain/commerce-registry/models/landing-dinamica.model';
import { mapGeneroApiToFormCode } from '../../../domain/commerce-registry/models/existing-user-profile.model';

function coercePartialThemeFromRow(row: LandingDinamicaRtdb): Partial<CommerceRegistryThemePalette> | undefined {
  const raw = row.commerce_theme ?? row.form_theme;
  if (raw == null || typeof raw !== 'object') {
    return undefined;
  }
  const o = raw as Record<string, unknown>;
  const partial: Partial<CommerceRegistryThemePalette> = {};
  if (typeof o['primary'] === 'string') partial.primary = o['primary'];
  if (typeof o['secondary'] === 'string') partial.secondary = o['secondary'];
  if (typeof o['tertiary'] === 'string') partial.tertiary = o['tertiary'];
  if (typeof o['textHint'] === 'string') partial.textHint = o['textHint'];
  if (typeof o['input'] === 'string') partial.input = o['input'];
  if (typeof o['typography'] === 'string' && o['typography'].trim()) {
    partial.typography = o['typography'].trim();
  }
  if (typeof o['backgroundImage'] === 'string' && o['backgroundImage'].trim()) {
    partial.backgroundImage = o['backgroundImage'].trim();
  }
  return Object.keys(partial).length > 0 ? partial : undefined;
}

function coerceDocumentTypes(row: LandingDinamicaRtdb): DocumentTypeOption[] {
  const raw = row['tipos_identificacion'];
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((t): t is Record<string, unknown> => t != null && typeof t === 'object')
    .map((t) => {
      const codeRaw = String(t['codigo'] ?? t['code'] ?? '').trim();
      const label = String(t['nombre'] ?? t['label'] ?? '').trim();
      const idFromRow =
        t['id'] ?? t['id_tipo'] ?? t['idTipo'] ?? t['tipo_id'];
      const validationTypeIdRaw =
        typeof idFromRow === 'number' && !Number.isNaN(idFromRow)
          ? String(idFromRow)
          : typeof idFromRow === 'string'
            ? idFromRow.trim()
            : undefined;
      const validationTypeId =
        validationTypeIdRaw && /^\d+$/.test(validationTypeIdRaw)
          ? validationTypeIdRaw
          : /^\d+$/.test(codeRaw)
            ? codeRaw
            : undefined;
      // Prefer numeric id as select value (matches CNF external_code); else siglas/code.
      const code = validationTypeId ?? codeRaw;
      return {
        code,
        label,
        ...(validationTypeId != null ? { validationTypeId } : {}),
      };
    })
    .filter((t) => t.code && t.label);
}

function optionalUrl(row: LandingDinamicaRtdb, key: string): string | undefined {
  const v = row[key];
  if (typeof v !== 'string') return undefined;
  const t = v.trim();
  return t ? t : undefined;
}

function coerceHomeActions(row: LandingDinamicaRtdb): HomeAction[] | undefined {
  const raw = row['home_actions'];
  if (!Array.isArray(raw)) return undefined;
  const out: HomeAction[] = [];
  for (const item of raw) {
    if (item == null || typeof item !== 'object') continue;
    const o = item as Record<string, unknown>;
    const icon = String(o['icon'] ?? '').trim();
    const label = String(o['label'] ?? '').trim();
    const url = String(o['url'] ?? '').trim();
    if (!icon || !label || !url) continue;
    out.push({ icon, label, url });
  }
  return out.length > 0 ? out : undefined;
}

/** Maps RTDB landing row to the commerce-registry UI model. */
export function mapLandingDinamicaToCommerceLanding(
  row: LandingDinamicaRtdb,
  fallbackSlug: string,
): CommerceLanding {
  const paises = Array.isArray(row.paises)
    ? row.paises.map((ip) => {
        const idRaw = ip?.idComercio;
        const idComercio =
          typeof idRaw === 'number' && !Number.isNaN(idRaw) ? idRaw : undefined;
        return {
          code: String(ip?.pais?.cod_pais ?? '').trim(),
          label: String(ip?.pais?.nombre ?? '').trim(),
          ...(idComercio != null ? { idComercio } : {}),
        };
      })
    : [];

  const generos = Array.isArray(row.generos)
    ? row.generos
        .map((g) => {
          const raw = String(g?.codGenero ?? '').trim();
          const value = mapGeneroApiToFormCode(raw) ?? raw;
          return {
            value,
            label: String(g?.nombre ?? '').trim(),
          };
        })
        .filter((g) => g.value !== '' && g.label !== '')
    : [];

  const partialTheme = coercePartialThemeFromRow(row);

  const programName = String(
    row['nombre_programa'] ?? row['nombre_puntos'] ?? row['etiqueta'] ?? '',
  ).trim();

  const plataformaOrigen = row['plataforma_origen'];
  const referenciaOrigen =
    typeof plataformaOrigen === 'string' && plataformaOrigen.trim()
      ? plataformaOrigen.trim()
      : undefined;

  const otpRaw = row['otp_habilitado'];
  const otp_habilitado = typeof otpRaw === 'boolean' ? otpRaw : undefined;

  // Explicit true only — omitted/false keeps safe rollout (no ms-consent POST).
  const msConsentRaw = row['ms_consent_habilitado'];
  const ms_consent_habilitado = msConsentRaw === true ? true : undefined;

  const witheRaw = row['withe_label'];
  const withe_label = witheRaw === true ? true : undefined;

  const homeViewRaw = row['home_view'];
  const home_view = typeof homeViewRaw === 'boolean' ? homeViewRaw : undefined;

  const home_actions = coerceHomeActions(row);

  const data_protection = optionalUrl(row, 'data_protection');
  const privacy_policy = optionalUrl(row, 'privacy_policy');
  const terms_conditions = optionalUrl(row, 'terms_conditions');

  return {
    comercio_slug: String(row.comercio_slug ?? fallbackSlug).trim() || fallbackSlug,
    programName,
    path_banner_lg: String(row.path_banner_lg ?? ''),
    path_banner_md: String(row.path_banner_md ?? ''),
    path_banner_sm: String(row.path_banner_sm ?? ''),
    logomarca: String(row.logomarca ?? ''),
    commerce_theme: partialTheme as CommerceRegistryThemePalette | undefined,
    paises,
    documentTypes: coerceDocumentTypes(row),
    generos,
    terminos: String(row.terminos ?? ''),
    ...(referenciaOrigen != null ? { referenciaOrigen } : {}),
    ...(otp_habilitado !== undefined ? { otp_habilitado } : {}),
    ...(ms_consent_habilitado === true ? { ms_consent_habilitado: true } : {}),
    ...(withe_label === true ? { withe_label: true } : {}),
    ...(home_view !== undefined ? { home_view } : {}),
    ...(home_actions != null ? { home_actions } : {}),
    ...(data_protection != null ? { data_protection } : {}),
    ...(privacy_policy != null ? { privacy_policy } : {}),
    ...(terms_conditions != null ? { terms_conditions } : {}),
  };
}
